import { useCallback, useEffect, useLayoutEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { toast } from 'sonner';
import type { Story } from '@/types/cmsTypes';
import type { ProjectInstance } from '@/types/projectTypes';
import { useAccountChanges, type AccountChangeEvent } from '@/modules/mcpStream';
import projectPlaceholder from '@/assets/project-historical.jpg';
import { loadProjectPage, loadProjectRow, type ProjectPageData, type ProjectPageStatus } from './loadProjectPage';

export type ProjectLoadState = 'loading' | 'ready' | 'not-found' | 'error';

type RefreshKind = 'full' | 'project';

const merge = (a: RefreshKind | null, b: RefreshKind): RefreshKind => (a === 'full' || b === 'full' ? 'full' : 'project');

const REFRESH_FAILED_TOAST = 'project-refresh-failed';

const idleRun = () => ({ seq: 0, busy: false, queued: null as RefreshKind | null, deferred: null as RefreshKind | null, afterWrite: null as RefreshKind | null });

const noKnownIds = () => ({ projectId: '', storyIds: new Set<string>(), sceneIds: new Set<string>(), imageIds: new Set<string>() });

const union = (a: Set<string>, b: Iterable<string>) => new Set([...a, ...b]);

// A project event alone re-reads the project row; a story, scene, image or link event the page cannot rule out reads everything
function refreshFor(events: AccountChangeEvent[], known: { projectId: string; storyIds: Set<string>; sceneIds: Set<string>; imageIds: Set<string> }): RefreshKind | null {
  if (events.length === 0) return 'full';
  let kind: RefreshKind | null = null;
  for (const event of events) {
    const id = event.entityUniqueId;
    switch (event.entity) {
      case 'project':
        if (!id || id === known.projectId) kind ??= 'project';
        break;
      case 'story':
      case 'shareLink':
        if (!id || known.storyIds.has(id) || (event.entity === 'story' && event.op === 'created')) return 'full';
        break;
      case 'scene':
        if (!id || known.sceneIds.has(id) || event.op === 'created') return 'full';
        break;
      case 'image':
        if (!id || known.imageIds.has(id) || event.op === 'created') return 'full';
        break;
    }
  }
  return kind;
}

export interface ProjectPageDataState {
  loadState: ProjectLoadState;
  loadError: Error | null;
  project: ProjectInstance | null;
  setProject: Dispatch<SetStateAction<ProjectInstance | null>>;
  stories: Story[];
  setStories: Dispatch<SetStateAction<Story[]>>;
  storiesError: Error | null;
  projectStatus: ProjectPageStatus;
  setProjectStatus: Dispatch<SetStateAction<ProjectPageStatus>>;
  statusUnknown: boolean;
  heroImage: string;
  heroImageCrop: unknown;
  reload: () => Promise<void>;
  // A refresh that overlaps this write is dropped when it lands and read again afterwards
  localWrite: <T>(write: () => Promise<T>) => Promise<T>;
}

export function useProjectPageData(id: string | undefined, userId: string | undefined, authLoading: boolean): ProjectPageDataState {
  const [loadState, setLoadState] = useState<ProjectLoadState>('loading');
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [project, setProject] = useState<ProjectInstance | null>(null);
  const [stories, setStories] = useState<Story[]>([]);
  const [storiesError, setStoriesError] = useState<Error | null>(null);
  const [projectStatus, setProjectStatus] = useState<ProjectPageStatus>('draft');
  const [statusUnknown, setStatusUnknown] = useState(false);
  const [heroImage, setHeroImage] = useState<string>(projectPlaceholder);
  const [heroImageCrop, setHeroImageCrop] = useState<unknown>(null);

  const shown = useRef(false);
  const statusKnown = useRef(false);
  const storiesRead = useRef(false);
  const known = useRef(noKnownIds());
  const writes = useRef({ inFlight: 0, epoch: 0 });
  const run = useRef(idleRun());

  // A background refresh whose story read failed keeps the stories shown
  const apply = useCallback((data: ProjectPageData, keepStories: boolean) => {
    setProject(data.project);
    if (data.hero) {
      setHeroImage(data.hero.url);
      setHeroImageCrop(data.hero.crop);
    }
    const prev = known.current;
    known.current = {
      projectId: data.project.id,
      storyIds: keepStories ? prev.storyIds : new Set(data.stories.map(s => s.id)),
      sceneIds: data.sceneIdsComplete ? new Set(data.sceneIds) : union(prev.sceneIds, data.sceneIds),
      imageIds: data.imageIdsComplete ? new Set(data.imageIds) : union(prev.imageIds, data.imageIds),
    };
    if (keepStories) return;
    storiesRead.current = data.storiesError === null;
    setStories(data.stories);
    setStoriesError(data.storiesError);
    setStatusUnknown(data.statusUnknown);
    // Unknown statuses keep the badge shown; before any is known, the server's published flag stands in
    if (data.status) {
      setProjectStatus(data.status);
      statusKnown.current = true;
    } else if (!statusKnown.current) {
      setProjectStatus(data.project.isPublished ? 'live' : 'draft');
    }
  }, []);

  const latestExecute = useRef<(kind: RefreshKind) => Promise<void>>(async () => {});

  const refreshFailed = useCallback(() => {
    toast.error("We couldn't refresh this project.", {
      id: REFRESH_FAILED_TOAST,
      action: { label: 'Retry', onClick: () => void latestExecute.current('full') },
    });
  }, []);

  const execute = useCallback(async (kind: RefreshKind): Promise<void> => {
    if (!id || !userId) return;
    const r = run.current;
    if (r.busy) {
      r.queued = merge(r.queued, kind);
      return;
    }
    r.busy = true;
    const seq = ++r.seq;
    const epoch = writes.current.epoch;
    // A read that overlapped a write on this page is dropped and read again once the writes are done
    const stillCurrent = () => {
      if (r !== run.current || seq !== r.seq) return false;
      if (epoch === writes.current.epoch && writes.current.inFlight === 0) return true;
      if (writes.current.inFlight > 0) r.afterWrite = merge(r.afterWrite, kind);
      else r.queued = merge(r.queued, kind);
      return false;
    };
    try {
      if (kind === 'project' && shown.current) {
        const row = await loadProjectRow(id, userId);
        if (!stillCurrent()) return;
        if (row) setProject(prev => (prev ? { ...row, totalDuration: prev.totalDuration } : row));
        return;
      }
      const result = await loadProjectPage(id, userId);
      if (!stillCurrent()) return;
      if (result.kind === 'ok') {
        const keepStories = storiesRead.current && result.data.storiesError !== null;
        apply(result.data, keepStories);
        shown.current = true;
        setLoadState('ready');
        setLoadError(null);
        if (keepStories) refreshFailed();
        else toast.dismiss(REFRESH_FAILED_TOAST);
      } else if (!shown.current) {
        setLoadState(result.kind);
        setLoadError(result.kind === 'error' ? result.error : null);
      } else if (result.kind === 'error') {
        // A failed background read keeps the page and what is open on it
        refreshFailed();
      } else {
        setLoadState('not-found');
      }
    } finally {
      r.busy = false;
      const next = r.queued;
      r.queued = null;
      if (next && r === run.current) void latestExecute.current(next);
    }
  }, [id, userId, apply, refreshFailed]);

  useLayoutEffect(() => {
    latestExecute.current = execute;
  });

  const request = useCallback((kind: RefreshKind) => {
    if (typeof document !== 'undefined' && document.hidden) {
      const r = run.current;
      r.deferred = merge(r.deferred, kind);
      return;
    }
    void execute(kind);
  }, [execute]);

  useEffect(() => {
    const onVisible = () => {
      if (document.hidden) return;
      const deferred = run.current.deferred;
      run.current.deferred = null;
      if (deferred) void execute(deferred);
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [execute]);

  const [loadInputs, setLoadInputs] = useState<{ id?: string; userId?: string; authLoading: boolean } | null>(null);
  if (!loadInputs || loadInputs.id !== id || loadInputs.userId !== userId || loadInputs.authLoading !== authLoading) {
    const otherProject = !!loadInputs && loadInputs.id !== id;
    setLoadInputs({ id, userId, authLoading });
    if (otherProject) {
      setProject(null);
      setStories([]);
    }
    if (!authLoading && userId && id) setLoadState(!otherProject && project ? 'ready' : 'loading');
    else if (!authLoading) setLoadState('not-found');
  }

  // Before the load below: another project starts from nothing
  useEffect(() => {
    shown.current = false;
    statusKnown.current = false;
    storiesRead.current = false;
    run.current = idleRun();
    known.current = noKnownIds();
  }, [id]);

  useEffect(() => {
    if (!authLoading && userId && id) void execute('full');
  }, [id, userId, authLoading, execute]);

  // The project, its stories, scenes, images or share links changed elsewhere (Claude through MCP, another tab)
  useAccountChanges(['project', 'story', 'scene', 'image', 'shareLink'], (events) => {
    if (authLoading || !userId || !id || !shown.current) return;
    const kind = refreshFor(events, known.current);
    if (kind) request(kind);
  }, 1000);

  const reload = useCallback(() => execute('full'), [execute]);

  const localWrite = useCallback(async <T,>(write: () => Promise<T>): Promise<T> => {
    const w = writes.current;
    w.inFlight += 1;
    w.epoch += 1;
    try {
      return await write();
    } finally {
      w.inFlight -= 1;
      w.epoch += 1;
      const after = run.current.afterWrite;
      if (w.inFlight === 0 && after) {
        run.current.afterWrite = null;
        void execute(after);
      }
    }
  }, [execute]);

  return {
    loadState, loadError, project, setProject, stories, setStories, storiesError,
    projectStatus, setProjectStatus, statusUnknown, heroImage, heroImageCrop, reload, localWrite,
  };
}
